import { ProductCategory } from "@grandvisionhq/svc-tealium/events";
import { mapProductTypeToProductCategory, formatPrice, mapProductStockToProductStatus } from "@grandvisionhq/svc-tealium/utils";
const TEALIUM_PRODUCT_INSURANCE_BENEFITS = "frames,lenses";
const getFinalStockCount = (availability) => {
  return availability ? Object.entries(availability.channels).reduce((prev, next) => {
    return prev + next[1].availableQuantity;
  }, 0) : 0;
};
const planeLensTypes = [
  ProductCategory.CONTACT_LENSES,
  ProductCategory.SUN,
  ProductCategory.OPTICS,
  ProductCategory.EYEWEAR
];
const mapVisualLineItemProductToTealiumProduct = (lineItems, withIndex) => {
  const products = {};
  lineItems.forEach((lineItem, index) => {
    const productKey = withIndex ? `${lineItem.sku}#${index}` : lineItem.sku;
    const stockCount = getFinalStockCount(lineItem.availability);
    const lensPackageItem = lineItem.type == "GLASSES_BUNDLE" && lineItem.bundleChildLineItems ? lineItem.bundleChildLineItems.filter(
      (item) => item.type == "PrescriptionGlassPackageOptions"
    ) : [];
    const category = lineItem.type == "GLASSES_BUNDLE" ? mapProductTypeToProductCategory(lineItem.subProductType) : mapProductTypeToProductCategory(lineItem.productType);
    const productDiscount = lineItem.unitPrice?.appliedDiscounts?.filter(
      (item) => item.type === "PRODUCT_DISCOUNT"
      /* PRODUCT_DISCOUNT */
    ).reduce((prev, next) => prev + next.value, 0);
    const price = lineItem?.unitPrice?.originalPrice ? formatPrice(lineItem?.unitPrice?.originalPrice - (productDiscount || 0), true) : "0";
    const quantityParts = lineItem.quantity.split(":") ?? [];
    const quantityValue = quantityParts[1] ?? "";
    const isPrescriptionGlasses = lineItem.productType === "prescription-glasses";
    products[productKey] = {
      Category: category,
      ...isPrescriptionGlasses ? {
        FrameType: "CP"
      } : {
        ModelName: lineItem.displayName || ""
      },
      LensType: isPrescriptionGlasses ? "RX" : planeLensTypes.includes(category) ? "PLANO" : void 0,
      Price: price,
      PriceFull: formatPrice(lineItem.unitPrice?.originalPrice || 0, true),
      ...lensPackageItem.length && lensPackageItem[0] ? { LensUPC: lensPackageItem[0].sku } : {},
      Status: mapProductStockToProductStatus(stockCount, true),
      TaxRate: formatPrice(lineItem.unitPrice?.taxRate || 0, false),
      Units: `${quantityValue.trim()}`,
      InsuranceBenefits: isPrescriptionGlasses && lineItem.insurance ? TEALIUM_PRODUCT_INSURANCE_BENEFITS : void 0,
      Warranty: formatPrice(lineItem.insurance?.price?.total.gross ?? 0, true)
    };
  });
  return products;
};
const mapLineItemProductToTealiumProduct = (lineItems, withIndex) => {
  const products = {};
  lineItems.map((lineItem, index) => {
    const key = lineItem.type == "prescription-glasses" ? lineItem.frame.sku : lineItem.sku;
    const stockCount = getFinalStockCount(lineItem.availability);
    const category = lineItem.productType === "Frames" || lineItem.productType === "optical" ? mapProductTypeToProductCategory(lineItem.productType.toLocaleLowerCase()) : mapProductTypeToProductCategory(lineItem.type);
    const itemIndex = key;
    const productDiscount = lineItem.price.unit?.appliedDiscounts?.filter(
      (item) => item.type === "PRODUCT_DISCOUNT"
      /* PRODUCT_DISCOUNT */
    ).reduce((prev, next) => prev + next.value, 0);
    const price = lineItem.price.unit.original ? formatPrice(lineItem.price.unit.original - (productDiscount || 0), true) : "0";
    const isPrescriptionGlasses = lineItem.productType === "prescription-glasses";
    products[itemIndex] = {
      Price: price,
      PriceFull: formatPrice(lineItem.price.unit.original, true),
      Category: category,
      ...isPrescriptionGlasses ? {
        FrameType: "CP"
      } : {
        ModelName: `${lineItem.name}`
      },
      LensType: isPrescriptionGlasses ? "RX" : planeLensTypes.includes(category) ? "PLANO" : void 0,
      Status: mapProductStockToProductStatus(stockCount, true),
      Units: `${lineItem.quantity}`,
      TaxRate: formatPrice(lineItem.price.unit.taxRate, false),
      InsuranceBenefits: isPrescriptionGlasses && lineItem.insurance ? TEALIUM_PRODUCT_INSURANCE_BENEFITS : void 0,
      Warranty: formatPrice(lineItem.insurance?.price?.total.gross ?? 0, true)
    };
  });
  return products;
};
const mapRemovedProductToTealiumProduct = (lineItem) => {
  const key = lineItem.type == "prescription-glasses" ? lineItem.frame.sku : lineItem.sku;
  const category = lineItem.productType === "Frames" ? mapProductTypeToProductCategory(lineItem.productType.toLocaleLowerCase()) : mapProductTypeToProductCategory(lineItem.type);
  return {
    [key]: {
      Category: category,
      ...lineItem.type == "prescription-glasses" ? { LensType: "RX", FrameType: "CP" } : { LensType: planeLensTypes.includes(category) ? "PLANO" : void 0 }
    }
  };
};
const cartToTealiumOrderDiscountInfo = (cart) => {
  const orderDiscount = cart.prices.appliedDiscounts?.filter(
    (item) => item.type === "CART_DISCOUNT"
    /* CART_DISCOUNT */
  ).reduce((prev, next) => prev + next.value, 0);
  return {
    Order_DiscountAmount: orderDiscount ? `-${formatPrice(orderDiscount, true)}` : "0",
    Order_DiscountCode: cart.voucherCodes?.length ? cart.voucherCodes.join() : "",
    Order_DiscountName: cart.voucherNames?.length ? cart.voucherNames.join() : ""
  };
};
const cartToTealiumPriceInfo = (cart) => {
  const shippingDiscount = cart?.prices.shippingDiscount || 0;
  const products = mapVisualLineItemProductToTealiumProduct(cart.visualLineItems);
  let Order_ProductsAmount = 0;
  Object.values(products).forEach((product) => {
    Order_ProductsAmount += parseFloat(product.Price) * parseFloat(product.Units);
  });
  return {
    Order_Subtotal: formatPrice(
      cart.prices.totalPrice - cart.prices.taxPrice - cart.prices.shippingPrice,
      true
    ),
    Order_Total: formatPrice(cart.prices.totalPrice, true),
    Order_TaxAmount: formatPrice(cart.prices.taxPrice, true),
    Order_ShippingTaxRate: formatPrice(cart.prices.shippingTaxRate, false),
    Order_ShippingDiscount: shippingDiscount ? `-${formatPrice(cart.prices.shippingDiscount, true)}` : "0",
    Order_ShippingAmount: formatPrice(cart.prices.shippingPrice, true),
    Order_ProductsAmount: formatPrice(Order_ProductsAmount, false)
  };
};
const getCartSubscriptionFrequency = (cart) => {
  if (!cart.hasSubscription) {
    return "0";
  } else {
    const subscriptionProduct = cart.lineItems.filter(
      (item) => item.type == "contact-lenses" && "subscriptionDetails" in item
    )[0];
    if (!subscriptionProduct) {
      return "0";
    } else {
      const subscription = subscriptionProduct.subscriptionDetails;
      if (!subscription?.deliveryInterval) return "0";
      return convertDaysToMonths(subscription?.deliveryInterval).toFixed(1);
    }
  }
};
const getCartToTealiumShippingInfo = (shippingMethod) => ({
  Order_ShippingMode: getShippingMode(shippingMethod),
  Order_ShippingType: getShippingType(shippingMethod)
});
const getShippingType = (shippingMethodKey) => {
  switch (shippingMethodKey) {
    case "home-delivery":
    case "dpd-priority-delivery":
    case "home-delivery-express":
      return "Home";
    case "nearest-pick-up":
    case "pick-up":
    case "pick-up-point":
      return "Store";
    default:
      return "Home";
  }
};
const getShippingMode = (shippingMethodKey) => {
  switch (shippingMethodKey) {
    case "dpd-priority-delivery":
    case "home-delivery-express":
      return "Express";
    case "nearest-pick-up":
    case "pick-up":
    case "pick-up-point":
      return "Pick-up-point";
    default:
      return "Standard";
  }
};
const convertDaysToMonths = (days) => {
  return days / 30;
};
export {
  cartToTealiumOrderDiscountInfo,
  cartToTealiumPriceInfo,
  getCartSubscriptionFrequency,
  getCartToTealiumShippingInfo,
  getFinalStockCount,
  mapLineItemProductToTealiumProduct,
  mapRemovedProductToTealiumProduct,
  mapVisualLineItemProductToTealiumProduct
};
//# sourceMappingURL=line-item-to-tealium-product.js.map
